import { AfterViewInit, Component, ContentChild, ElementRef, EventEmitter, input, Input, OnDestroy, OnInit, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { dispose, ECharts, EChartsOption, init } from 'echarts';
import { SeriesType } from './utils/SeriesType';
import { Chart } from './builder';
import { BasicChart } from './utils/models';
import { Subscription } from 'rxjs';
import { debounceTime, skip, switchMap } from 'rxjs/operators';
import { GeneralService } from '../../services/general.service';
import { EssentialObjectService } from '../../services/essential-object.service';
import { ECHARTS_DARK_THEME, ThemeService } from 'src/app/shared/Theme/services/theme.service';



enum RenderType {
  Canvas = 'canvas',
  SVG = 'svg'
}
@Component({
  selector: 'zen-chart',
  templateUrl: './e-chart.component.html',
  styleUrls: ['./e-chart.component.scss'],
  host: { 'id': 'unique-id' },
  standalone: false
})
export class EChartComponent implements AfterViewInit, OnDestroy {
  @Input('data') chart!: Chart;
  @Input() width: number = 25;
  @Input() height: number = 31.25;
  @Input() exportPageRows: number = 21;
  private subscriptions = new Subscription();
  rowView: boolean = false;
  @Output() exportPageLength: EventEmitter<any> = new EventEmitter();
  private resizeListener!: () => void;
  @ViewChild('chartSelector') chartSelector!: ElementRef;
  @ContentChild('row') rowTemplate!: TemplateRef<any>;
  private eChart!: ECharts;
  private currentType?: string;
  export: boolean = false;
  _chart: any;
  customChart: any;
  chartAvailable: boolean = true;
  private wasRowView = false;
  renderAsImage: boolean = false;
  chartImageDataUrl: string | null = null;
  @Input() language: string = 'EN';
  hasCountValue: boolean;
  private lastOptions: any = null;
  constructor(private elementRef: ElementRef, private renderer: Renderer2, private generalService: GeneralService, private themeService: ThemeService) {
    this.subscriptions.add(
      generalService.resize$.pipe(debounceTime(100)).subscribe(() => this.eChart?.resize())
    );
    this.subscriptions.add(
      themeService.current$.pipe(skip(1)).subscribe(() => this.applyThemeChange())
    );
  }

  private isInsideExport(): boolean {
    return !!this.elementRef.nativeElement.closest('.exportPage12, .export-root, kendo-pdf-document');
  }

  private resolveTheme(): string | null {
    if (this.isInsideExport()) return null;
    return this.themeService.current === 'dark' ? ECHARTS_DARK_THEME : null;
  }

  private applyThemeChange(): void {
    if (!this.chartSelector || !this.eChart || this.isInsideExport()) return;
    this.eChart.dispose();
    this.eChart = init(this.chartSelector.nativeElement, this.resolveTheme(), {
      renderer: this.chart?.export ? RenderType.SVG : RenderType.Canvas,
      useDirtyRect: !this.chart?.export
    });
    if (this.lastOptions) {
      this.eChart.setOption(this.lastOptions);
      this.eChart.on('click', params => { this.chart?.onClick?.(params); });
    }
  }

  ngAfterViewInit(): void {
    this.eChart = init(this.chartSelector.nativeElement, this.resolveTheme(), { renderer: RenderType.Canvas });

    this.subscriptions.add(
      this.chart.chart$.pipe(debounceTime(200)).subscribe(options => {
        // 


        const categories = this.chart.type == SeriesType.Bar ? options?.yAxis?.data : options?.xAxis?.data || [];
        if (this.chart.type == SeriesType.Doughnut)
          this.chart.type = SeriesType.Pie;
        if ((this.chart.export && categories!.length > 6 || this.chart.export && categories!.find(x => x.length > 15) || this.chart.custom) && this.chart.autoCustomChart) {
          this.setUpCustomChart(options);
          return;
        }

        this.rowView = false;

        // const tooltip = options.tooltip as {};

        const chartOptions: BasicChart = { ...options }


        chartOptions.series = chartOptions?.series?.map((series: any) => {
          if (series.type == SeriesType.Column)
            series.type = SeriesType.Bar;
          return series;
        })
        // 

        if (this.chart?.categories?.length > 6) {
          if (this.chart.export) {
            this.rowView = true;
          } else {
            chartOptions.dataZoom?.forEach(x => {
              const isBarChart = this.chart.type === SeriesType.Bar;
              x.yAxisIndex = isBarChart ? 0 : null;
              x.xAxisIndex = isBarChart ? null : 0;
              x.disabled = false;
              x.zoomOnMouseWheel = true;
              x.show = true;
            });
          }
        } else if (this.chart?.export || this.chart?.categories.length <= 6) {
          chartOptions.dataZoom?.forEach(x => {
            x.disabled = true;
            x.zoomOnMouseWheel = false;
            x.show = false;
            x.start = 0;
            x.end = 100;
          });

          if (chartOptions.xAxis) {
            chartOptions.xAxis.axisLabel = {
              ...chartOptions.xAxis.axisLabel,
              interval: 0,
              hideOverlap: true,
              rotate: 45
            };
          }
        }

        if (this.chart.type == SeriesType.Pie) {
          chartOptions.tooltip = { trigger: 'item' }
          chartOptions.yAxis = undefined;
          chartOptions.xAxis = undefined;
        }

        // Add default tooltip formatting with 1 decimal + % if no custom formatter is provided
        if (chartOptions.tooltip && !(chartOptions.tooltip as any).formatter) {
          if (this.chart.type === SeriesType.Pie) {
            (chartOptions.tooltip as any).formatter = (params: any) => {
              const value = params.value?.value ?? params.value;
              const val = parseFloat(value);
              const formatted = isNaN(val) ? value : val.toFixed(1) + '%';
              return `${params.marker} ${params.name}: <b>${formatted}</b>`;
            };
          } else {
            (chartOptions.tooltip as any).valueFormatter = (chartOptions.tooltip as any).valueFormatter ?? ((value: any) => {
              const v = value?.value ?? value;
              const val = parseFloat(v);
              return isNaN(val) ? v : `${val.toFixed(1)}%`;
            });
          }
        }

        if (chartOptions?.series[0]?.type == SeriesType.Gauge) {
          chartOptions.grid = {};
        } else {
          let bottom = 20, right = 20, top = 30;
          if ((this.chart.type == SeriesType.Column || this.chart.type == SeriesType.Line) && !!chartOptions.dataZoom)
            bottom += 30;
          if (this.chart.type == SeriesType.Bar && !!chartOptions.dataZoom)
            right += 60;

          if (chartOptions?.legend?.data?.length) {
            const exportFactor = this.chart.export ? 4 : 7;
            chartOptions.legend.top = 15
            top += 10 + ((chartOptions.legend.data.length / exportFactor) * 20);
          }
          chartOptions.grid = { top, bottom, left: 30, right, containLabel: true };
        }


        // ---- DYNAMIC GRID CALCULATION END ----





        if (this.shouldRerender(chartOptions) || !!this.export != !!this.chart.export) {
          this.eChart.dispose();
          this.eChart = init(this.chartSelector.nativeElement, this.resolveTheme(), {
            renderer: this.chart.export ? RenderType.SVG : RenderType.Canvas,
            useDirtyRect: !this.chart.export // Canvas optimization for non-export mode
          });
          setTimeout(() => {
            requestAnimationFrame(() => {
              this.eChart.resize();
            });
          }, 1000);


        }

        // this.eChart.clear()

        this.export = this.chart.export;

        //console.log(chartOptions);
        this.chartAvailable = !!chartOptions.series[0]?.data?.length;
        if (!this.chartAvailable) {
          return;
        }
        const finalOptions = {
          ...chartOptions as any,
          animation: this.chart.export ? false : true // Explicitly enable/disable animations
        };
        this.lastOptions = finalOptions;
        this.eChart.setOption(finalOptions, {
          replaceMerge: ['series', 'xAxis', 'yAxis'],
          lazyUpdate: this.chart.export // Batch updates in export mode
        });

        // Convert to static image when in export mode and not a custom chart
        if (this.chart.export && !this.chart.custom && this.chartAvailable) {
          this.captureChartAsImage();
        } else {
          this.renderAsImage = false;
          this.chartImageDataUrl = null;
        }

        console.log(chartOptions);
        this.eChart.on('click', params => {
          this.chart?.onClick?.(params);
        });


      })
    );

  }

  ngAfterViewChecked() {
    // if (this.wasRowView && !this.rowView && this.chartAvailable) {
    //   // Chart just became visible again
    //   requestAnimationFrame(() => {
    //     this.eChart?.resize();
    //   });
    // }
    // this.wasRowView = this.rowView;
  }


  setUpCustomChart(options) {

    // 
    this._chart = options;
    const isBarChart = this.chart?.type === SeriesType.Bar;
    const categories: string[] = isBarChart ? options?.yAxis?.data : options?.xAxis?.data;

    if (!categories || !categories.length) {
      this.customChart = [];
      this.rowView = false;
      return;
    }

    const seriesList = options?.series ?? [];
    const rowsPerPage = this.exportPageRows;

    // Map to hold structured row data by category
    const categoryMap = new Map<string, { category: string; values: any[] }>();

    // Initialize the map with categories
    categories.forEach(category => {
      categoryMap.set(category, { category, values: [] });
    });
    this.hasCountValue = false;

    // Add each series item into the correct category row
    seriesList.forEach(series => {
      const seriesName = series.name ?? '';

      series.data.forEach((item: any, index: number) => {
        const category = categories[index];
        const value = item?.value ?? null;
        if (item?.value1) {
          this.hasCountValue = true;
        }
        if (categoryMap.has(category)) {
          categoryMap.get(category)!.values.push({
            series: seriesName,
            itemStyle: series.itemStyle,
            value,
            value1: item?.value1,
            data: item
          });
        }
      });
    });

    // Convert map to rows array, sorted by the value in values property
    const rows = Array.from(categoryMap.values()).sort((a, b) => {
      const valA = Number(a.values[0]?.value) || 0;
      const valB = Number(b.values[0]?.value) || 0;
      return valB - valA;
    });
    // Paginate the result
    this.customChart = [];
    if (rows.length > 0) {
      for (let i = 0; i < rows.length; i += rowsPerPage) {
        this.customChart.push(rows.slice(i, i + rowsPerPage));
      }
    }

    this.rowView = this.customChart.length > 0;
    this.exportPageLength.emit(this.customChart.length);
    this.export = this.chart?.export;
    console.log(this.customChart);
  }

  onCustomChartRowClick(row: any, rowIndex: number) {
    this.chart?.onClick?.({
      name: row.category,
      value: row.values?.[0]?.value,
      data: row,
      seriesName: row.values?.[0]?.series,
      dataIndex: rowIndex
    } as any);
  }


  /**
   * Captures the chart as a static image for export mode.
   * This improves PDF export performance by replacing the live chart
   * with a static image that matches the chart's dimensions.
   */
  private captureChartAsImage(): void {
    // Small delay to ensure chart is fully rendered before capturing
    setTimeout(() => {
      if (this.eChart && !this.eChart.isDisposed()) {
        try {
          const dataUrl = this.eChart.getDataURL({
            type: 'png',
            pixelRatio: 2, // Higher quality for export
            backgroundColor: '#fff'
          });
          this.chartImageDataUrl = dataUrl;
          this.renderAsImage = true;
        } catch (e) {
          console.warn('Failed to capture chart as image:', e);
          this.renderAsImage = false;
        }
      }
    }, 300);
  }

  private shouldRerender(options: any): boolean {
    const newType = this.chart.type;
    const isPieOrBar = (type) => type === SeriesType.Pie || type === SeriesType.Bar;
    const needsRerender = (isPieOrBar(newType) || isPieOrBar(this.currentType)) && newType !== this.currentType;
    this.currentType = newType;
    return needsRerender;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.resizeListener) this.resizeListener();
    this.eChart.dispose();
  }

  logData(e) {
    console.log(e);
  }
}
